import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ShieldAlert, X, Cpu, MemoryStick, HardDrive } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/**
 * SystemStatsAlert - SuperAdmin-only banner showing live server load.
 *
 * Behaviour:
 *   • Polls GET /api/admin/system-stats every 60s.
 *   • Hidden when level === "ok" (all metrics below 75%).
 *   • Amber banner when level === "warn" (any metric 75-89%).
 *   • Red banner when level === "crit" (any metric >= 90%).
 *   • User can manually dismiss the banner; it re-appears on level escalation
 *     (warn → crit) or after a full hour, whichever comes first.
 *
 * Mounted globally inside AppShellTopNav so it covers every authenticated
 * agency / SA route. Non-SuperAdmins never see it and the polling is skipped
 * entirely to avoid useless 403 traffic.
 */

const POLL_INTERVAL_MS = 60_000;
// Auto-resurface a dismissed banner after this much time (60 min). Keeps the
// SA aware even if they dismissed it once.
const DISMISS_RESURFACE_MS = 60 * 60 * 1000;

export default function SystemStatsAlert() {
  const { user } = useAuth();
  const isSA = user?.role === 'superadmin';
  const [stats, setStats] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(0);
  const lastLevelRef = useRef('ok');

  useEffect(() => {
    if (!isSA) return undefined;
    let alive = true;

    const tick = async () => {
      try {
        const { data } = await api.get('/admin/system-stats');
        if (!alive) return;
        setStats(data);
        // Re-show banner if level escalated from "warn" to "crit" even if
        // the SA had dismissed it previously.
        if (data?.level === 'crit' && lastLevelRef.current !== 'crit') {
          setDismissedAt(0);
        }
        // Clear dismissal once the resurface window has elapsed.
        setDismissedAt((prev) => (prev && Date.now() - prev >= DISMISS_RESURFACE_MS ? 0 : prev));
        lastLevelRef.current = data?.level || 'ok';
      } catch (e) {
        // Silent failure — endpoint is best-effort, we don't want to flood
        // logs every minute if it's transiently down.
      }
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [isSA]);

  if (!isSA || !stats) return null;
  const level = stats.level || 'ok';
  if (level === 'ok') return null;

  // Honour dismissal (the polling effect clears the flag once the resurface
  // window elapses, so a non-zero value here means the SA still wants it
  // hidden).
  if (dismissedAt) {
    return null;
  }

  const isCrit = level === 'crit';
  const palette = isCrit
    ? {
        wrap: 'bg-rose-600 text-white border-rose-700',
        accent: 'bg-rose-700/40',
        Icon: ShieldAlert,
        title: 'Server sotto carico critico',
        subtitle: 'CPU o RAM oltre il 90%. Aumenta CPU/RAM del container per evitare rallentamenti agli utenti.',
      }
    : {
        wrap: 'bg-amber-500 text-zinc-900 border-amber-600',
        accent: 'bg-amber-600/30',
        Icon: AlertTriangle,
        title: 'Carico server elevato',
        subtitle: 'Una o più risorse superano il 75%. Monitora e valuta un upgrade se la situazione persiste.',
      };

  const Icon = palette.Icon;
  const metrics = [
    { label: 'CPU', value: stats.cpu_percent, icon: Cpu },
    { label: 'RAM', value: stats.mem_percent, icon: MemoryStick },
    { label: 'Disco', value: stats.disk_percent, icon: HardDrive },
  ];

  return (
    <div
      className={`border-b ${palette.wrap} shadow-sm`}
      data-testid="sa-system-stats-banner"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <Icon className="h-5 w-5 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-semibold leading-tight" data-testid="sa-system-stats-title">
            {palette.title}
          </div>
          <div className={`text-xs ${isCrit ? 'text-white/85' : 'text-zinc-800/80'} leading-snug`}>
            {palette.subtitle}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="sa-system-stats-metrics">
          {metrics.map((m) => {
            const MIcon = m.icon;
            const val = typeof m.value === 'number' ? m.value : 0;
            const flagged = val >= 75;
            return (
              <span
                key={m.label}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  flagged ? palette.accent : 'bg-white/30'
                }`}
                data-testid={`sa-system-stats-metric-${m.label.toLowerCase()}`}
              >
                <MIcon className="h-3 w-3" />
                {m.label} {val.toFixed(0)}%
              </span>
            );
          })}
          {typeof stats.mongo_ping_ms === 'number' && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/30"
              title="Round-trip ping a MongoDB"
              data-testid="sa-system-stats-metric-mongo"
            >
              DB {stats.mongo_ping_ms} ms
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissedAt(Date.now())}
          className={`h-7 w-7 inline-flex items-center justify-center rounded-full transition-colors ${
            isCrit ? 'hover:bg-white/15' : 'hover:bg-black/10'
          }`}
          aria-label="Chiudi avviso"
          title="Chiudi (riapparirà se peggiora o dopo 1 ora)"
          data-testid="sa-system-stats-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
